'use client'

import { useEffect, useState } from 'react'
import { Link } from '@/i18n/navigation'
import { ChevronRight } from 'lucide-react'
import { GatedBadge } from '@/components/content/content-gate'
import { difficultyValueToParam } from '@/lib/content/difficulty'
import { optimizeSanityImageUrl } from '@/lib/sanity/client'
import { useTranslations } from 'next-intl'
import type {
  MegaMenuTutorialsData,
  MegaMenuCasData,
  MegaMenuFloggersData,
  MegaMenuStoreData,
} from '@/lib/sanity/mega-menu-data'

interface FeaturedItem {
  title: string
  href: string
  thumbnail?: string
  author?: string
  isGated?: boolean
  category?: string
}

interface ColumnDef {
  /** Stable difficulty value — used to key the cas-featured data
   *  lookup AND as the basis for the URL param. Distinct from
   *  `label`, which is the (translated, now "Easy" for the easiest
   *  tier) display string. Keying data off the label broke once the
   *  label diverged from the value. */
  key: string
  label: string
  href: string
  color: string
}

interface SectionDef {
  /** Stable, untranslated identifier for the section — 'body-parts',
   *  'difficulty', 'specialty'. The desktop tutorials panel renders a
   *  dedicated column per section, so it has to look sections up by
   *  something that doesn't move when the label is translated. Optional
   *  for back-compat; the lookup falls back to the old label heuristic. */
  key?: string
  label: string
  items: { label: string; href: string; color?: string; image?: string }[]
}

/**
 * Item in a `cards` mega-menu panel — community / resources style.
 * Renders as a label + description card, no image. The optional `icon`
 * is a lucide-react component class (e.g. `Compass`); we accept a
 * generic React component to avoid coupling this file to any one icon
 * library.
 */
type CardItem = {
  label: string
  description: string
  href: string
  icon?: React.ComponentType<{ className?: string }>
}

interface MegaMenuPanelProps {
  label: string
  href: string
  tabs: { label: string; href: string }[]
  featured?: FeaturedItem[]
  banners?: { label: string; href: string; backgroundImage?: string }[]
  categories?: { label: string; description: string; href: string }[]
  columns?: ColumnDef[]
  sections?: SectionDef[]
  cards?: CardItem[]
  floggerLayout?: boolean
  storeLayout?: boolean
  /** Render the Courses-specific 3-column-by-difficulty panel. */
  coursesLayout?: boolean
  showSkeleton?: boolean
  hasAccess?: boolean
  onNavigate?: () => void
  /** Server-prefetched data for the sections/columns/flogger/store layouts. */
  prefetchedTutorials?: MegaMenuTutorialsData | null
  prefetchedCas?: MegaMenuCasData | null
  prefetchedFloggers?: MegaMenuFloggersData | null
  prefetchedStore?: MegaMenuStoreData | null
}

export function MegaMenuPanel({ label, href, tabs, featured, banners, categories, columns, sections, cards, floggerLayout, storeLayout, coursesLayout, showSkeleton = true, hasAccess = false, onNavigate, prefetchedTutorials, prefetchedCas, prefetchedFloggers, prefetchedStore }: MegaMenuPanelProps) {

  // ─── Cards layout: community / resources style ──────────────
  // A clean grid of label + description cards, matching the visual
  // weight of the body-part / difficulty cards on tutorials. No
  // images, no featured side panel — just a tidy launchpad.
  if (cards && cards.length > 0) {
    return <CardsLayout label={label} href={href} cards={cards} onNavigate={onNavigate} />
  }

  // ─── Store layout: foundations book + physical + partners split ──
  if (storeLayout) {
    return <StoreLayout href={href} onNavigate={onNavigate} prefetched={prefetchedStore} />
  }

  // ─── Flogger layout: designs + knots split ────────────────
  if (floggerLayout) {
    return <FloggerLayout href={href} hasAccess={hasAccess} onNavigate={onNavigate} prefetched={prefetchedFloggers} />
  }

  // ─── Courses layout: 3 columns by difficulty ──────────────
  if (coursesLayout) {
    return <CoursesLayout hasAccess={hasAccess} onNavigate={onNavigate} />
  }

  // ─── Sections layout: body part cards + difficulty columns ──
  if (sections && sections.length > 0) {
    return <TutorialSectionsLayout label={label} href={href} sections={sections} hasAccess={hasAccess} onNavigate={onNavigate} prefetched={prefetchedTutorials} />
  }

  // ─── Column layout: 3 difficulty columns with cards ──────────
  if (columns && columns.length > 0) {
    return <ColumnLayout label={label} href={href} columns={columns} hasAccess={hasAccess} onNavigate={onNavigate} prefetched={prefetchedCas} />
  }

  // ─── Split layout: categories left + featured right ──────────
  if (categories && categories.length > 0) {
    return (
      <div className="w-full border-b bg-white shadow-lg">
        <div className="mx-auto max-w-7xl px-4 py-6">
          <div className="flex gap-8">
            {/* Left: Category links */}
            <div className="w-64 shrink-0 border-r border-gray-100 pr-8">
              <nav className="space-y-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.label}
                    href={cat.href}
                    onClick={onNavigate}
                    className="group flex items-center justify-between py-3 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-bold text-brand-700 group-hover:text-brand-900 uppercase tracking-wide">
                        {cat.label}
                      </p>
                      <p className="text-xs text-gray-500 group-hover:text-gray-700">
                        {cat.description}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-brand-400 shrink-0 ml-2 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                ))}
              </nav>
            </div>

            {/* Right: Featured items */}
            <div className="flex-1 min-w-0">
              <p className="mb-4 text-[10px] font-semibold uppercase tracking-wider text-brand-600">
                Featured Posts
              </p>
              {featured && featured.length > 0 ? (
                <div className="grid grid-cols-4 gap-3">
                  {featured.slice(0, 4).map((item) => (
                    <Link
                      key={item.title}
                      href={item.href}
                      onClick={onNavigate}
                      className="group relative overflow-hidden bg-gray-100 aspect-[3/4]"
                    >
                      {item.thumbnail ? (
                        <img
                          src={optimizeSanityImageUrl(item.thumbnail, 320, { height: 427, fit: 'crop' })}
                          alt={item.title}
                          className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-105"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-3xl font-bold text-gray-300">
                          {item.title.charAt(0)}
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="text-xs font-semibold text-white line-clamp-2 drop-shadow">
                          {item.title}
                        </p>
                        {item.author && (
                          <p className="mt-0.5 text-[10px] text-white/70">{item.author}</p>
                        )}
                      </div>
                      {/* Top-right: category chip or gated lock */}
                      {item.category ? (
                        <span className="absolute top-2 right-2 bg-white/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-700 backdrop-blur-sm shadow-sm">
                          {item.category}
                        </span>
                      ) : item.isGated && !hasAccess ? (
                        <GatedBadge className="absolute top-2 right-2" />
                      ) : null}
                    </Link>
                  ))}
                </div>
              ) : showSkeleton ? (
                <div className="grid grid-cols-4 gap-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="aspect-[3/4] bg-gray-100 animate-pulse" />
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/* Bottom: See all link */}
          <div className="mt-5 border-t border-gray-100 pt-4">
            <Link
              href={href}
              onClick={onNavigate}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 uppercase tracking-wide hover:text-brand-900 transition-colors"
            >
              <span className="h-5 w-0.5 bg-brand-600" />
              See All {label}{label.endsWith('s') ? '' : 's'}
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // ─── Default layout: tabs + featured grid ────────────────────
  return (
    <div className="w-full border-b bg-white shadow-lg">
      {/* Category tabs */}
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex items-center gap-1 py-3 overflow-x-auto scrollbar-hide">
          {tabs.map((tab, i) => (
            <Link
              key={tab.label}
              href={tab.href}
              onClick={onNavigate}
              className={`whitespace-nowrap px-5 py-2 text-sm font-medium transition-colors ${
                i === 0
                  ? 'bg-brand-700 text-white'
                  : 'text-gray-600 hover:bg-brand-50 hover:text-brand-700'
              }`}
            >
              {tab.label}
            </Link>
          ))}
          <Link
            href={href}
            onClick={onNavigate}
            className="ml-auto whitespace-nowrap border border-gray-300 px-5 py-2 text-sm font-medium text-gray-600 hover:border-brand-400 hover:bg-brand-50 hover:text-brand-700 transition-colors"
          >
            All {label}
          </Link>
        </div>
      </div>

      {/* Featured items */}
      <div className="mx-auto max-w-7xl px-4 py-6">
        {featured && featured.length > 0 ? (
          <div className="bg-gray-50 p-6">
            <div className="grid grid-cols-5 gap-8">
              {featured.map((item) => (
                <Link
                  key={item.title}
                  href={item.href}
                  onClick={onNavigate}
                  className="group flex flex-col items-center gap-3 text-center"
                >
                  <div className="relative h-28 w-28 overflow-hidden border-2 border-gray-200 group-hover:border-brand-500 transition-colors shadow-sm">
                    {item.thumbnail ? (
                      <img
                        src={optimizeSanityImageUrl(item.thumbnail, 224, { height: 224, fit: 'crop' })}
                        alt={item.title}
                        className="h-full w-full object-cover transition-transform group-hover:scale-110"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gray-100 text-2xl font-bold text-gray-300">
                        {item.title.charAt(0)}
                      </div>
                    )}
                    {item.isGated && !hasAccess && (
                      <GatedBadge className="absolute bottom-1 right-1" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 group-hover:text-brand-700 transition-colors line-clamp-2">
                      {item.title}
                    </p>
                    {item.author && (
                      <p className="mt-0.5 text-xs text-gray-500">{item.author}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        ) : showSkeleton ? (
          <div className="bg-gray-50 p-8">
            <div className="grid grid-cols-5 gap-8">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex flex-col items-center gap-3">
                  <div className="h-28 w-28 bg-gray-200 animate-pulse" />
                  <div className="h-3 w-20 bg-gray-200 animate-pulse" />
                  <div className="h-2 w-14 bg-gray-100 animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {/* Banner cards */}
      {banners && banners.length > 0 && (
        <div className="mx-auto max-w-7xl px-4 pb-6">
          <div className="grid grid-cols-2 gap-4">
            {banners.map((banner) => (
              <Link
                key={banner.label}
                href={banner.href}
                onClick={onNavigate}
                className="group relative overflow-hidden h-36"
              >
                {banner.backgroundImage ? (
                  <img
                    src={optimizeSanityImageUrl(banner.backgroundImage, 800, { height: 288, fit: 'crop' })}
                    alt={banner.label}
                    className="absolute inset-0 h-full w-full object-cover transition-transform group-hover:scale-105"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-100 to-purple-100" />
                )}
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/5 transition-colors" />
                <div className="relative flex h-full items-end p-4">
                  <h3 className="text-lg font-bold text-white drop-shadow">{banner.label}</h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Cards Layout (community / resources) ─────────────────────
//
// Each item gets its own card with an optional icon, the label, a
// short description, and a chevron. Visually mirrors the categories
// layout but is laid out as a 2- or 3-column grid (responsive on
// the count) so 4-item Community and 6-item Resources both look
// balanced.
function CardsLayout({
  label,
  href,
  cards,
  onNavigate,
}: {
  label: string
  href: string
  cards: CardItem[]
  onNavigate?: () => void
}) {
  // 2 cols when ≤4 cards, 3 cols when more — keeps each card a
  // comfortable size at the standard panel width.
  const gridCols = cards.length <= 4 ? 'grid-cols-2' : 'grid-cols-3'

  return (
    <div className="w-full border-b bg-white shadow-lg">
      <div className="mx-auto max-w-7xl px-4 py-6">
        <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-gray-500">
          {label}
        </p>
        <div className={`grid ${gridCols} gap-3`}>
          {cards.map((card) => {
            const Icon = card.icon
            return (
              <Link
                key={card.label}
                href={card.href}
                onClick={onNavigate}
                className="group flex items-start gap-3 border border-gray-100 bg-white p-4 transition-all hover:border-brand-200 hover:shadow-sm hover:bg-brand-50/30"
              >
                {Icon && (
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-brand-50 text-brand-700 transition-colors group-hover:bg-brand-100">
                    <Icon className="h-5 w-5" />
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 group-hover:text-brand-700 transition-colors">
                    {card.label}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500 line-clamp-2 group-hover:text-gray-700 transition-colors">
                    {card.description}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-300 transition-all group-hover:translate-x-0.5 group-hover:text-brand-500" />
              </Link>
            )
          })}
        </div>

        {/* Bottom: See all link — only render when href is real (Resources
            uses href="#" because it has no landing page). */}
        {href && href !== '#' && (
          <div className="mt-5 border-t border-gray-100 pt-4">
            <Link
              href={href}
              onClick={onNavigate}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 uppercase tracking-wide hover:text-brand-900 transition-colors"
            >
              <span className="h-5 w-0.5 bg-brand-600" />
              See All {label}
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Column Layout (CAS difficulty grid) ──────────────────────

type ColumnItem = { title: string; href: string; thumbnail: string | null; isGated: boolean }

function ColumnLayout({
  href,
  columns,
  hasAccess,
  onNavigate,
  prefetched,
}: {
  label: string
  href: string
  columns: ColumnDef[]
  hasAccess: boolean
  onNavigate?: () => void
  prefetched?: MegaMenuCasData | null
}) {
  const [grouped, setGrouped] = useState<Record<string, ColumnItem[]>>(prefetched || {})
  const [loaded, setLoaded] = useState<boolean>(prefetched != null)

  useEffect(() => {
    if (prefetched != null) return
    fetch('/api/sanity/cas-featured')
      .then((r) => r.json())
      .then((data) => {
        setGrouped(data || {})
        setLoaded(true)
      })
      .catch(() => setLoaded(true))
  }, [prefetched])

  const t = useTranslations('navMenu')

  return (
    <div className="w-full border-b bg-white shadow-lg">
      <div className="mx-auto max-w-7xl px-4 py-6">
        {/* 3-column grid — one column per difficulty tier
            (Easy / Intermediate / Advanced). Was grid-cols-4 when an
            Expert tier existed; dropped to 3 with the 2026-05
            difficulty change. */}
        <div className="grid grid-cols-3 gap-6">
          {columns.map((col) => {
            // Key the data lookup on the stable difficulty value
            // (col.key), not the display label — the cas-featured
            // API groups by 'beginner' / 'intermediate' / 'advanced'.
            const items = grouped[col.key] || []
            return (
              <div key={col.key}>
                {/* Column header */}
                <Link
                  href={col.href}
                  onClick={onNavigate}
                  className="group mb-4 flex items-center gap-2"
                >
                  <span
                    className="h-6 w-1 shrink-0"
                    style={{ background: col.color }}
                  />
                  <span
                    className="text-sm font-bold uppercase tracking-wide group-hover:opacity-80 transition-opacity"
                    style={{ color: col.color }}
                  >
                    {col.label}
                  </span>
                  <ChevronRight
                    className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-70 group-hover:translate-x-0 transition-all"
                    style={{ color: col.color }}
                  />
                </Link>

                {/* Cards — 2 compact items per difficulty. Was a tall
                    "1 large hero + 2 small" stack; trimmed to a single
                    row of two so the Create-a-Scene panel isn't oversized. */}
                {!loaded ? (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="aspect-square bg-gray-100 animate-pulse" />
                    <div className="aspect-square bg-gray-100 animate-pulse" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="flex aspect-[2/1] items-center justify-center bg-gray-50 text-xs text-gray-300">
                    {t('comingSoon')}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {items.slice(0, 2).map((item) => (
                      <Link
                        key={item.title}
                        href={item.href}
                        onClick={onNavigate}
                        className="group/card relative block overflow-hidden aspect-square bg-gray-100"
                      >
                        {item.thumbnail ? (
                          <img
                            src={optimizeSanityImageUrl(item.thumbnail, 300, { height: 300, fit: 'crop' })}
                            alt={item.title}
                            className="absolute inset-0 h-full w-full object-cover transition-transform group-hover/card:scale-105"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-lg font-bold text-gray-300">
                            {item.title.charAt(0)}
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-1.5">
                          <p className="text-[10px] font-medium text-white line-clamp-2 drop-shadow-sm">
                            {item.title}
                          </p>
                        </div>
                        {item.isGated && !hasAccess && (
                          <GatedBadge size="sm" className="absolute top-1 right-1" />
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Bottom: See all link */}
        <div className="mt-5 border-t border-gray-100 pt-4">
          <Link
            href={href}
            onClick={onNavigate}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 uppercase tracking-wide hover:text-brand-900 transition-colors"
          >
            <span className="h-5 w-0.5 bg-brand-600" />
            See All Scenes
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  )
}

// ─── Tutorial Sections Layout ────────────────────────────────────

type TutorialFeaturedItem = { title: string; href: string; thumbnail: string | null; isGated: boolean }

// `key` is the stored difficulty value (also the cas-featured /
// tutorials-featured data-lookup key) AND the `filters.*` message key for
// its localized label — the same chips the grids use. The easiest tier
// keeps key 'beginner' but is labelled "Easy"; the 'expert' tier was
// dropped (2026-05 client request).
const DIFFICULTY_META: { key: 'beginner' | 'intermediate' | 'advanced'; color: string }[] = [
  { key: 'beginner', color: '#16a34a' },
  { key: 'intermediate', color: '#d97706' },
  { key: 'advanced', color: '#dc2626' },
]


function TutorialSectionsLayout({
  href,
  sections,
  onNavigate,
  prefetched,
}: {
  label: string
  href: string
  sections: SectionDef[]
  hasAccess: boolean
  onNavigate?: () => void
  prefetched?: MegaMenuTutorialsData | null
}) {
  const [data, setData] = useState<{
    bodyPartCounts: Record<string, number>
    difficulties: Record<string, TutorialFeaturedItem[]>
  } | null>(prefetched ?? null)

  useEffect(() => {
    if (prefetched != null) return
    fetch('/api/sanity/tutorials-featured')
      .then((r) => r.json())
      .then(setData)
      .catch(() => {})
  }, [prefetched])

  const t = useTranslations('navMenu')
  const tFilter = useTranslations('filters')

  // Look the columns up by their stable `key`; fall back to the original
  // label heuristic so a caller that hasn't been updated still renders.
  const bodyPartSection =
    sections.find((s) => s.key === 'body-parts') ??
    sections.find((s) => s.label.toLowerCase().includes('body'))
  const difficultySection =
    sections.find((s) => s.key === 'difficulty') ??
    sections.find((s) => s.label.toLowerCase().includes('difficulty'))
  const specialtySection = sections.find((s) => s.key === 'specialty')

  return (
    <div className="w-full border-b bg-white shadow-lg">
      <div className="mx-auto max-w-7xl px-4 py-6">
        {/* Three columns: body-part cards (fluid) + difficulty rows +
            specialty rows. The two right-hand rails are fixed-width and
            step down at `lg` so the fluid body-part grid still gets a
            usable share of the 1024px panel (≈150px per card there,
            ≈185px from `xl` up). */}
        <div className="flex gap-6 xl:gap-8">
          {/* ─── Left: Body Part Cards ─────────────────────── */}
          <div className="flex-1 min-w-0">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-gray-500">
              {bodyPartSection?.label || 'By Body Part'}
            </p>
            <div className="grid grid-cols-3 gap-2">
              {(bodyPartSection?.items || []).map((item) => {
                return (
                  <Link
                    key={item.label}
                    href={item.href}
                    onClick={onNavigate}
                    className="group relative flex flex-col justify-end overflow-hidden p-4 h-44 xl:h-56 border border-gray-100 transition-all hover:border-brand-200 hover:shadow-sm bg-gradient-to-br from-gray-50 to-gray-100"
                  >
                    {item.image && (
                      <>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={optimizeSanityImageUrl(item.image, 600, { height: 500, fit: 'crop' })}
                          alt=""
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        {/* Bottom-up gradient so the label stays readable on
                            any image. Slightly darker on hover for contrast. */}
                        <span
                          aria-hidden
                          className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent"
                        />
                      </>
                    )}
                    <p
                      className={`relative text-sm font-semibold transition-colors ${
                        item.image
                          ? 'text-white drop-shadow group-hover:text-white'
                          : 'text-gray-800 group-hover:text-brand-700'
                      }`}
                    >
                      {item.label}
                    </p>
                  </Link>
                )
              })}
            </div>
          </div>

          {/* ─── Middle: Difficulty Rows ───────────────────── */}
          <div className="w-[300px] xl:w-[380px] shrink-0">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-gray-500">
              {difficultySection?.label || 'By Difficulty'}
            </p>
            <div className="space-y-2">
              {DIFFICULTY_META.map((diff) => {
                const items = data?.difficulties?.[diff.key] || []
                const firstItem = items[0]
                return (
                  <Link
                    key={diff.key}
                    href={`/tutorials?difficulty=${difficultyValueToParam(diff.key)}`}
                    onClick={onNavigate}
                    className="group flex items-center gap-3 border border-gray-100 p-6 transition-all hover:border-brand-200 hover:shadow-sm"
                  >
                    <div className="flex items-center gap-2.5 w-28 shrink-0">
                      <span
                        className="h-16 w-1 shrink-0"
                        style={{ background: diff.color }}
                      />
                      <p
                        className="text-sm font-bold uppercase tracking-wide"
                        style={{ color: diff.color }}
                      >
                        {tFilter(diff.key)}
                      </p>
                    </div>

                    {firstItem?.thumbnail ? (
                      <div className="h-24 w-24 overflow-hidden bg-gray-100 shrink-0">
                        <img
                          src={optimizeSanityImageUrl(firstItem.thumbnail, 192, { height: 192, fit: 'crop' })}
                          alt=""
                          className="h-full w-full object-cover transition-transform group-hover:scale-110"
                        />
                      </div>
                    ) : !data ? (
                      <div className="h-24 w-24 bg-gray-100 animate-pulse shrink-0" />
                    ) : null}

                    <div className="flex-1 min-w-0">
                      {firstItem ? (
                        <p className="text-xs text-gray-600 line-clamp-2 group-hover:text-gray-900 transition-colors">
                          {firstItem.title}
                        </p>
                      ) : !data ? (
                        <div className="h-3 w-24 bg-gray-100 animate-pulse" />
                      ) : (
                        <p className="text-xs text-gray-300">{t('comingSoon')}</p>
                      )}
                    </div>

                    <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-brand-500 shrink-0 transition-colors" />
                  </Link>
                )
              })}
            </div>
          </div>

          {/* ─── Right: Specialty ──────────────────────────── */}
          {/* One row per specialty (Fashion, Self Tie, Suspension,
              Accessibility, Drills…). Rows are pure config from the
              `specialty` section in buildNavItems; each lands on the
              tutorials grid pre-filtered via `?specialty=`, so this
              column needs no fetched data. */}
          {specialtySection && specialtySection.items.length > 0 && (
            <div className="w-[180px] xl:w-[230px] shrink-0 flex flex-col">
              <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                {specialtySection.label}
              </p>
              <div className="flex-1 space-y-2">
                {specialtySection.items.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={onNavigate}
                    className="group flex items-center justify-between gap-2 border border-gray-100 px-3 py-2.5 transition-all hover:border-brand-200 hover:shadow-sm"
                  >
                    <span className="text-xs font-medium text-gray-700 group-hover:text-gray-900 transition-colors">
                      {item.label}
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-brand-500 shrink-0 transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom: See all link */}
        <div className="mt-5 border-t border-gray-100 pt-4">
          <Link
            href={href}
            onClick={onNavigate}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 uppercase tracking-wide hover:text-brand-900 transition-colors"
          >
            <span className="h-5 w-0.5 bg-brand-600" />
            See All Tutorials
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  )
}

// ─── Flogger Layout (Designs + Knots split) ─────────────────────

type FloggerItem = { title: string; href: string; thumbnail: string | null; difficulty: string | null; isGated: boolean }

function FloggerLayout({
  href,
  hasAccess,
  onNavigate,
  prefetched,
}: {
  href: string
  hasAccess: boolean
  onNavigate?: () => void
  prefetched?: MegaMenuFloggersData | null
}) {
  const [data, setData] = useState<{ designs: FloggerItem[]; knots: FloggerItem[] } | null>(
    prefetched ?? null,
  )

  useEffect(() => {
    if (prefetched != null) return
    fetch('/api/sanity/floggers-featured')
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ designs: [], knots: [] }))
  }, [prefetched])

  const t = useTranslations('navMenu')

  const renderCard = (item: FloggerItem) => (
    <Link
      key={item.title}
      href={item.href}
      onClick={onNavigate}
      className="group/card relative block overflow-hidden bg-gray-100 aspect-[4/3]"
    >
      {item.thumbnail ? (
        <img
          src={optimizeSanityImageUrl(item.thumbnail, 400, { height: 300, fit: 'crop' })}
          alt={item.title}
          className="absolute inset-0 h-full w-full object-cover transition-transform group-hover/card:scale-105"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-gray-300">
          {item.title.charAt(0)}
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="text-xs font-semibold text-white line-clamp-2 drop-shadow-sm">
          {item.title}
        </p>
        {item.difficulty && (
          <p className="mt-0.5 text-[10px] text-white/70 capitalize">{item.difficulty}</p>
        )}
      </div>
      {item.isGated && !hasAccess && (
        <GatedBadge className="absolute top-2 right-2" />
      )}
    </Link>
  )

  const renderSkeleton = () => (
    <div className="grid grid-cols-2 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="aspect-[4/3] bg-gray-100 animate-pulse" />
      ))}
    </div>
  )

  return (
    <div className="w-full border-b bg-white shadow-lg">
      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="flex gap-8">
          {/* ─── Left: Flogger Designs ─────────────────────── */}
          <div className="flex-1 min-w-0">
            <div className="mb-3">
              {/* Section header — the "View All" affordance was
                  removed; the entire Flogger nav item already
                  routes to /floggers, so a second link inside the
                  panel was redundant. */}
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                Flogger Designs
              </p>
            </div>
            {!data ? renderSkeleton() : (
              <div className="grid grid-cols-2 gap-3">
                {data.designs.length > 0
                  ? data.designs.map(renderCard)
                  : <p className="col-span-2 text-center text-xs text-gray-300 py-8">{t('comingSoon')}</p>
                }
              </div>
            )}
          </div>

          {/* ─── Divider ──────────────────────────────────── */}
          <div className="w-px bg-gray-100 shrink-0" />

          {/* ─── Right: Flogger Knots ─────────────────────── */}
          <div className="flex-1 min-w-0">
            <div className="mb-3">
              {/* "View All" link removed — see comment above on the
                  Designs column. The Floggers top-level nav handles
                  the catch-all destination. */}
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
                Flogger Knots
              </p>
            </div>
            {!data ? renderSkeleton() : (
              <div className="grid grid-cols-2 gap-3">
                {data.knots.length > 0
                  ? data.knots.map(renderCard)
                  : <p className="col-span-2 text-center text-xs text-gray-300 py-8">{t('comingSoon')}</p>
                }
              </div>
            )}
          </div>
        </div>

        {/* Bottom: See all link */}
        <div className="mt-5 border-t border-gray-100 pt-4">
          <Link
            href={href}
            onClick={onNavigate}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 uppercase tracking-wide hover:text-brand-900 transition-colors"
          >
            <span className="h-5 w-0.5 bg-brand-600" />
            See All Floggers
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  )
}

// ─── Courses Layout (3 columns: Beginner / Intermediate / Advanced) ─────
// Same visual vocabulary as FloggerLayout — each card uses an
// aspect-[4/3] thumbnail with a gradient bottom strip for the title,
// a difficulty caption, and the brand-700 lock chip in the top-right
// for gated content. Difficulty drives the column rather than the
// card chip so the panel reads as "pick where you are in your
// journey" rather than a flat grid.

type CourseItem = {
  title: string
  href: string
  thumbnail: string | null
  difficulty: string | null
  isGated: boolean
}

interface CoursesData {
  courses: CourseItem[]
}

function CoursesLayout({
  hasAccess,
  onNavigate,
}: {
  hasAccess: boolean
  onNavigate?: () => void
}) {
  const [data, setData] = useState<CoursesData | null>(null)

  useEffect(() => {
    fetch('/api/sanity/courses-featured')
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ courses: [] }))
  }, [])

  const t = useTranslations('navMenu')

  const renderCard = (item: CourseItem) => (
    <Link
      key={item.href}
      href={item.href}
      onClick={onNavigate}
      className="group/card relative block overflow-hidden bg-gray-100 aspect-[4/3]"
    >
      {item.thumbnail ? (
        <img
          src={optimizeSanityImageUrl(item.thumbnail, 400, { height: 300, fit: 'crop' })}
          alt={item.title}
          className="absolute inset-0 h-full w-full object-cover transition-transform group-hover/card:scale-105"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-gray-300">
          {item.title.charAt(0)}
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="text-xs font-semibold text-white line-clamp-2 drop-shadow-sm">
          {item.title}
        </p>
        {item.difficulty && (
          <p className="mt-0.5 text-[10px] capitalize text-white/70">{item.difficulty}</p>
        )}
      </div>
      {item.isGated && !hasAccess && (
        <GatedBadge className="absolute top-2 right-2" />
      )}
    </Link>
  )

  const renderSkeleton = () => (
    <div className="grid grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="aspect-[4/3] bg-gray-100 animate-pulse" />
      ))}
    </div>
  )

  return (
    <div className="w-full border-b bg-white shadow-lg">
      <div className="mx-auto max-w-7xl px-4 py-6">
        <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-gray-500">
          Featured Courses
        </p>
        {!data ? (
          renderSkeleton()
        ) : data.courses.length > 0 ? (
          <div className="grid grid-cols-4 gap-3">
            {data.courses.map(renderCard)}
          </div>
        ) : (
          <p className="text-center text-xs text-gray-300 py-8">{t('comingSoon')}</p>
        )}

        {/* View All — bottom strip, matching the pattern shared with
            Tutorials / CAS / Store mega-menu panels. */}
        <div className="mt-4 flex justify-end">
          <Link
            href="/courses"
            onClick={onNavigate}
            className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-brand-700 hover:text-brand-900 transition-colors"
          >
            View All Courses
            <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  )
}

// ─── Store Layout (Foundations Book + Physical + Partners) ────────

type StoreDigital = {
  title: string
  href: string
  thumbnail: string | null
  price: number | null
  originalPrice: number | null
}
type StorePhysical = {
  title: string
  href: string
  thumbnail: string | null
  price: number
  currencyCode: string
}
type StorePartner = {
  id: string
  name: string
  category: string
  logo: string | null
  buyUrl: string
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'CA$',
  AUD: 'A$',
}

function formatPrice(amount: number, code = 'USD') {
  const symbol = CURRENCY_SYMBOLS[code] ?? `${code} `
  return `${symbol}${amount.toFixed(2)}`
}

const FOUNDATIONS_MATCH = 'foundations-of-rope-bondage'

// Print is sold via Amazon — not Shopify, not Sanity. Hardcoded
// because there's exactly one print SKU and no plan to vary it.
const FOUNDATIONS_PRINT_AMAZON_URL =
  'https://www.amazon.com/s?k=Foundations+of+Rope+Bondage'

function StoreLayout({
  href,
  onNavigate,
  prefetched,
}: {
  href: string
  onNavigate?: () => void
  prefetched?: MegaMenuStoreData | null
}) {
  const t = useTranslations('navMenu')
  const [data, setData] = useState<{
    digital: StoreDigital[]
    physical: StorePhysical[]
    partners: StorePartner[]
  } | null>(prefetched ?? null)

  useEffect(() => {
    if (prefetched != null) return
    fetch('/api/sanity/store-featured')
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ digital: [], physical: [], partners: [] }))
  }, [prefetched])

  const foundationsEbook = data?.digital.find((d) =>
    d.href.toLowerCase().includes(FOUNDATIONS_MATCH),
  )

  return (
    <div className="w-full border-b bg-white shadow-lg">
      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="flex gap-8">
          {/* ─── Foundations of Rope Bondage Book — eBook + Print ── */}
          <div className="flex-1 min-w-0">
            <SectionHeader
              label={t('foundationsBook')}
              viewAllHref="/store#digital"
              onNavigate={onNavigate}
            />
            {!data ? (
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div key={i} className="aspect-[4/3] bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <FoundationsCard
                  format="eBook"
                  href={foundationsEbook?.href}
                  title={foundationsEbook?.title}
                  thumbnail={foundationsEbook?.thumbnail ?? null}
                  price={foundationsEbook?.price ?? null}
                  originalPrice={foundationsEbook?.originalPrice ?? null}
                  onNavigate={onNavigate}
                />
                <FoundationsCard
                  format="Print"
                  href={FOUNDATIONS_PRINT_AMAZON_URL}
                  external
                  title={t('books.foundations.title')}
                  subtitle={t('books.foundations.subtitle')}
                  thumbnail={null}
                  price={null}
                  onNavigate={onNavigate}
                />
              </div>
            )}
          </div>

          {/* ─── Divider ──────────────────────────────────── */}
          <div className="w-px bg-gray-100 shrink-0" />

          {/* ─── Physical — stacked thumbnail rows (Shopify) ── */}
          <div className="w-[300px] shrink-0">
            <SectionHeader
              label={t('physicalProducts')}
              viewAllHref="/store#physical"
              onNavigate={onNavigate}
            />
            {!data ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-14 bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : data.physical.length === 0 ? (
              <p className="text-center text-xs text-gray-300 py-8">{t('comingSoon')}</p>
            ) : (
              <div className="space-y-2">
                {data.physical.map((item) => (
                  <Link
                    key={item.title}
                    href={item.href}
                    onClick={onNavigate}
                    className="group flex items-center gap-3 border border-gray-100 p-2 transition-all hover:border-brand-200 hover:shadow-sm"
                  >
                    <div className="h-12 w-12 overflow-hidden bg-gray-100 shrink-0">
                      {item.thumbnail ? (
                        <img
                          src={item.thumbnail}
                          alt=""
                          className="h-full w-full object-cover transition-transform group-hover:scale-110"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-sm font-bold text-gray-300">
                          {item.title.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 line-clamp-2 group-hover:text-brand-700 transition-colors">
                        {item.title}
                      </p>
                      <p className="mt-0.5 text-[11px] font-bold text-brand-700">
                        {formatPrice(item.price, item.currencyCode)}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-brand-500 shrink-0 transition-colors" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* ─── Divider ──────────────────────────────────── */}
          <div className="w-px bg-gray-100 shrink-0" />

          {/* ─── Partners — verified external companies (logo grid) ── */}
          <div className="w-[260px] shrink-0">
            <SectionHeader
              label={t('partners')}
              viewAllHref="/store#partners"
              onNavigate={onNavigate}
            />
            {!data ? (
              <div className="grid grid-cols-3 gap-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="aspect-square bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : data.partners.length === 0 ? (
              <p className="text-center text-xs text-gray-300 py-8">{t('comingSoon')}</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {data.partners.slice(0, 6).map((p) => (
                  <a
                    key={p.id}
                    href={p.buyUrl}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    onClick={onNavigate}
                    title={`${p.name} — ${p.category}`}
                    className="group flex aspect-square items-center justify-center border border-gray-100 bg-white p-2 transition-all hover:border-brand-300 hover:shadow-sm"
                  >
                    {p.logo ? (
                      <img
                        src={optimizeSanityImageUrl(p.logo, 160, { height: 160, fit: 'max' })}
                        alt={p.name}
                        className="h-full w-full object-contain transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <span className="text-[10px] font-semibold text-gray-600 text-center line-clamp-3">
                        {p.name}
                      </span>
                    )}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bottom: See all link */}
        <div className="mt-5 border-t border-gray-100 pt-4">
          <Link
            href={href}
            onClick={onNavigate}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 uppercase tracking-wide hover:text-brand-900 transition-colors"
          >
            <span className="h-5 w-0.5 bg-brand-600" />
            {t('visitStore')}
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  )
}

function FoundationsCard({
  format,
  href,
  external,
  title,
  subtitle,
  thumbnail,
  price,
  originalPrice,
  currencyCode,
  onNavigate,
}: {
  format: 'eBook' | 'Print'
  href?: string
  external?: boolean
  title?: string
  subtitle?: string
  thumbnail: string | null
  price: number | null
  originalPrice?: number | null
  currencyCode?: string
  onNavigate?: () => void
}) {
  const t = useTranslations('navMenu')
  const cardClass =
    'group/card relative block overflow-hidden bg-gray-100 aspect-[4/3]'

  if (!href) {
    return (
      <div className={`${cardClass} flex items-center justify-center`}>
        <span className="absolute top-2 left-2 bg-gray-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-600">
          {format}
        </span>
        <span className="text-xs text-gray-400">{t('comingSoon')}</span>
      </div>
    )
  }

  const inner = (
    <>
      {thumbnail ? (
        <img
          src={optimizeSanityImageUrl(thumbnail, 400, { height: 300, fit: 'crop' })}
          alt={title ?? format}
          className="absolute inset-0 h-full w-full object-cover transition-transform group-hover/card:scale-105"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-gray-300">
          {format.charAt(0)}
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
      <span className="absolute top-2 left-2 bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-700 backdrop-blur-sm shadow-sm">
        {format}
      </span>
      <div className="absolute bottom-0 left-0 right-0 p-3">
        <p className="text-[11px] font-semibold text-white line-clamp-2 drop-shadow-sm">
          {title ?? 'Foundations of Rope Bondage'}
        </p>
        {subtitle ? (
          <p className="mt-0.5 text-[10px] text-white/80 drop-shadow-sm">{subtitle}</p>
        ) : null}
      </div>
      {price !== null ? (
        <span className="absolute top-2 right-2 bg-white/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand-700 backdrop-blur-sm shadow-sm">
          {originalPrice ? (
            <>
              <span className="line-through text-gray-400 mr-1">
                {formatPrice(originalPrice)}
              </span>
              {formatPrice(price)}
            </>
          ) : (
            formatPrice(price, currencyCode)
          )}
        </span>
      ) : null}
    </>
  )

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={onNavigate}
        className={cardClass}
      >
        {inner}
      </a>
    )
  }

  return (
    // prefetch={false}: the mega-menu renders many of these detail-page cards
    // at once; viewport-prefetching them all fires a burst of full RSC payloads
    // on every menu open. The card still navigates fine on click.
    <Link href={href} onClick={onNavigate} className={cardClass} prefetch={false}>
      {inner}
    </Link>
  )
}

function SectionHeader({
  label,
  viewAllHref,
  onNavigate,
}: {
  label: string
  viewAllHref: string
  onNavigate?: () => void
}) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">
        {label}
      </p>
      <Link
        href={viewAllHref}
        onClick={onNavigate}
        className="inline-flex items-center gap-1 text-[10px] font-semibold text-brand-600 uppercase tracking-wide hover:text-brand-800 transition-colors"
      >
        View All
        <ChevronRight className="h-3 w-3" />
      </Link>
    </div>
  )
}
